import React, { useState, useRef, useEffect } from 'react';
import { useCookies } from 'react-cookie';
import axios from 'axios';
import {faker} from '@faker-js/faker';
import { 
    Flex, Box, Heading, FormControl, FormLabel, Input, Button, Select, CircularProgress, CircularProgressLabel,
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton, Center, useToast,
} from '@chakra-ui/react';
import { MdSave, MdThumbUpAlt } from 'react-icons/md';
import ReCAPTCHA from 'react-google-recaptcha';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import validator from "validator";
import { Form, Field } from 'react-final-form';
import { PENDING_STATUS, PRICING_LIST, REGISTER_COMPANY, ERROR_CODES } from "../../../constants";
import { displayConfirmAlert } from "../../utils";
import DropZoneWithImage from '../../../components/DropZoneWithImage';

function RegisterCompanyModal({ 
    initialRef, isOpen, onClose, register_type
}) {
    const navigate = useNavigate();
    const captchaRef = useRef(null);
    const [ isHuman, setIsHuman ] = useState(false);
    const [ isValidPhone, setIsValidPhone ] = useState(true);
    const [ isValidEmail, setIsValidEmail ] = useState(true);
    const [ logoFile, setLogoFile ] = useState(null);
    const [ isProcessing, setIsProcessing ] = useState(false);
    const toast = useToast();

    const handleChangeCaptcha = async(value) => {
        try
        {
            let response = await axios.post(`${process.env.REACT_APP_WEB_SERVER_URL}/authenticateReCaptcha`, {token: value}, {
                auth: {
                    username: process.env.REACT_APP_BASIC_AUTH_USERNAME,
                    password: process.env.REACT_APP_BASIC_AUTH_PASSWORD
                }
            })
            if(response.data && response.data.action === "Human") {
                setIsHuman(true);
            } else {
                setIsHuman(false);
            }
        }
        catch(error) {
            if(error?.code) {
                toast({
                    description: `${ERROR_CODES[error?.code]} (${error?.code})`,
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                    position: "bottom-right"
                });
            }
        }
    }

    const validate = (values) => {
        const errors = {};

        if(!values.name) {
            errors.name = "Required";
        }
        if(!values.owner) {
            errors.owner = "Required";
        }
        if(!values.address) {
            errors.address = "Required";
        }
        if(!values.phone) {
            errors.phone = "Required";
        }
        if(!values.email) {
            errors.email = "Required";
        }
        if(!logoFile) {
            errors.logoFile = "Required";
        }
        
        if(values.phone?.length > 0) {
            const isValidPhone = validator.isMobilePhone(values.phone, "vi-VN");
            setIsValidPhone(isValidPhone);
            if(!isValidPhone) {
                errors.validPhone = "Required";
            }
        }

        if(values.email?.length > 0) {
            const isValidEmail = validator.isEmail(values.email);
            setIsValidEmail(isValidEmail);
            if(!isValidEmail) {
                errors.validEmail = "Required";
            }
        }

        return errors;
    }

    const onSubmit = async(values) => {
        setIsProcessing(true);
        
        try
        {
            let logo_url = null;

            if(logoFile) {
                // upload logo
                let logoFiledata = new FormData();
                logoFiledata.append('uri', logoFile);

                let resUpload = await axios.post(`${process.env.REACT_APP_API_ENDPOINT_URL}/upload?subPath=companies`, logoFiledata, { 
                    headers: { "Content-Type": "multipart/form-data" },
                    auth: {
                        username: process.env.REACT_APP_BASIC_AUTH_USERNAME,
                        password: process.env.REACT_APP_BASIC_AUTH_PASSWORD
                    }
                });

                if(resUpload.data?.status === 200) {
                    logo_url = resUpload.data.fileName;
                }
            }

            const TOTAL_MONTH_ACTIVED = PRICING_LIST[register_type]?.total_month_actived; 
            const MAX_USER_REGISTER = PRICING_LIST[register_type]?.max_user_register;
            const REGISTER_CODE = faker.string.alphanumeric(8).toUpperCase();

            const createNewCompanyRes = await axios.post(`${process.env.REACT_APP_API_ENDPOINT_URL}/companies`, {
                ...values,
                logo_url,
                is_active: false,
                register_type,
                register_code: REGISTER_CODE,
                register_code_expired: moment(new Date()).add("60", "minutes").toDate(),
                active_expired: moment(new Date()).add(TOTAL_MONTH_ACTIVED, "month").toDate(),
                max_user_register: MAX_USER_REGISTER
            }, {
                auth: {
                    username: process.env.REACT_APP_BASIC_AUTH_USERNAME,
                    password: process.env.REACT_APP_BASIC_AUTH_PASSWORD
                }
            });
                    
            if(createNewCompanyRes.data?.status === 200) {
                // FREE
                if(register_type === "FREE") {
                    await axios.post(`${process.env.REACT_APP_API_ENDPOINT_URL}/emails/new-register-company`, {
                        companyName: values.name,
                        companyEmail: values.email,
                        register_code: REGISTER_CODE,
                    }, {
                        auth: {
                            username: process.env.REACT_APP_BASIC_AUTH_USERNAME,
                            password: process.env.REACT_APP_BASIC_AUTH_PASSWORD
                        }
                    });
        
                    displayConfirmAlert(`
                        Quý khách đã đăng ký gói '${PRICING_LIST[register_type]?.label}' thành công. 
                        Tiếp tục tạo tài khoản để đăng nhập vào phần mềm.`,
                        () => {
                            navigate(`/create_new_user/${REGISTER_CODE}`);
                        }
                    );
                }
                // PAY 
                else {
                    const ORDER_CODE = faker.number.int({max: 9999999999});
                    const AMOUNT = PRICING_LIST[register_type]?.amount;
                    const DESCRIPTION = `Phí đăng ký ${ORDER_CODE}`;

                    const createPaymentLinkRes = await axios.post(`${process.env.REACT_APP_WEB_SERVER_URL}/create_payment_link_payos`, {
                        orderCode: ORDER_CODE,
                        amount: AMOUNT,
                        description: DESCRIPTION,
                        returnUrl: `${process.env.REACT_APP_URL}/payment_response/${REGISTER_COMPANY}/success`,
                        cancelUrl: `${process.env.REACT_APP_URL}/payment_response/${REGISTER_COMPANY}/failed`
                    }, {
                        auth: {
                            username: process.env.REACT_APP_BASIC_AUTH_USERNAME,
                            password: process.env.REACT_APP_BASIC_AUTH_PASSWORD
                        }
                    });

                    if(createPaymentLinkRes.data?.status === 200) {
                        // add new payment
                        await axios.post(`${process.env.REACT_APP_WEB_SERVER_URL}/payments`, {
                            company_id: createNewCompanyRes.data?.data?.id,
                            payment_type: REGISTER_COMPANY,
                            order_code: ORDER_CODE,
                            amount: AMOUNT,
                            description: DESCRIPTION,
                            status: PENDING_STATUS,
                            extra_data: {
                                ...values,
                                register_type,
                                register_code: REGISTER_CODE
                            },
                        }, {
                            auth: {
                                username: process.env.REACT_APP_BASIC_AUTH_USERNAME,
                                password: process.env.REACT_APP_BASIC_AUTH_PASSWORD
                            }
                        });

                        // open link to pay
                        window.open(createPaymentLinkRes.data?.data.checkoutUrl, "_self");
                    } else {
                        displayConfirmAlert(`Tạo thanh toán không thành công. Quý khách vui lòng thử lại hoặc liên hệ với phòng khám để được hỗ trợ!`);
                    }
                }
            } else {
                toast({
                    description: createNewCompanyRes?.data?.msg,
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                    position: "bottom-right"
                });
            }
        }
        catch(error) {
            if(error?.code) {
                toast({
                    description: `${ERROR_CODES[error?.code]} (${error?.code})`,
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                    position: "bottom-right"
                });
            }
        }
        
        setIsProcessing(false);
    }

    return (
        <React.Fragment>
            <Modal initialFocusRef={initialRef} isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false} size="md">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        ĐĂNG KÝ MỚI PHÒNG KHÁM
                    </ModalHeader>
                    <ModalCloseButton />

                    <ModalBody pb={2}>
                        <Form
                            onSubmit={onSubmit}
                            validate={validate}
                            render={({ handleSubmit, form, submitting, pristine, valid, values }) => {
                                return (
                                    <form onSubmit={handleSubmit}>
                                        <Flex direction="column">
                                            <FormControl isRequired>
                                                <FormLabel>Gói dịch vụ</FormLabel>
                                                <Select value={register_type} isReadOnly={true}>
                                                    {
                                                        Object.keys(PRICING_LIST).map(key => (
                                                            <React.Fragment key={key}>
                                                                <option value={key}>{PRICING_LIST[key]?.label}</option>
                                                            </React.Fragment>
                                                        ))
                                                    }
                                                </Select>
                                            </FormControl>

                                            <Field name="name">
                                                {({ input, meta }) => (
                                                    <FormControl isRequired >
                                                        <FormLabel>Tên phòng khám (tối đa 25 chữ)</FormLabel>
                                                        <Input {...input} type='text' placeholder='Nhập tên phòng khám' maxLength={25} />
                                                    </FormControl>
                                                )}
                                            </Field>

                                            <Field name="owner">
                                                {({ input, meta }) => (
                                                    <FormControl isRequired>
                                                        <FormLabel>Người Đại Diện</FormLabel>
                                                        <Input {...input} type='text' placeholder='Nhập người đại diện' />
                                                    </FormControl>
                                                )}
                                            </Field>

                                            <Field name="address">
                                                {({ input, meta }) => (
                                                    <FormControl isRequired>
                                                        <FormLabel>Địa chỉ</FormLabel>
                                                        <Input {...input} type='text' placeholder='Nhập địa chỉ' />
                                                    </FormControl>
                                                )}
                                            </Field>
                                            
                                            <Field name="phone">
                                                {({ input, meta }) => (
                                                    <FormControl isRequired>
                                                        <FormLabel>Số Điện Thoại</FormLabel>
                                                        <Input {...input} type='text' placeholder='Nhập số điện thoại' />
                                                        {
                                                            !isValidPhone &&
                                                            <FormLabel color="red">Nhập đúng Số Điện Thoại</FormLabel>
                                                        }
                                                    </FormControl>
                                                )}
                                            </Field>

                                            <Field name="email">
                                                {({ input, meta }) => (
                                                    <FormControl isRequired>
                                                        <FormLabel>Email</FormLabel>
                                                        <Input {...input} type='email' placeholder='Nhập email' />
                                                        {
                                                            !isValidEmail &&
                                                            <FormLabel color="red">Nhập đúng Email</FormLabel>
                                                        }
                                                    </FormControl>
                                                )}
                                            </Field>

                                            <FormControl mt={2} isRequired>
                                                <FormLabel>Logo:</FormLabel>
                                                <DropZoneWithImage callback={setLogoFile}/>
                                            </FormControl>

                                            <Field name="alarm_phone">
                                                {({ input, meta }) => (
                                                    <FormControl>
                                                        <FormLabel>Số điện thoại cấp cứu</FormLabel>
                                                        <Input {...input} type="tel" placeholder='Nhập số điện thoại cấp cứu' />
                                                    </FormControl>
                                                )}
                                            </Field>
        
                                            <Field name="work_time">
                                                {({ input, meta }) => (
                                                    <FormControl>
                                                        <FormLabel>Giờ làm việc (Ví dụ: 07:00 - 17:00)</FormLabel>
                                                        <Input {...input} type='text' placeholder='Nhập giờ làm việc' />
                                                    </FormControl>
                                                )}
                                            </Field>

                                            <Center mt={2}>
                                                <ReCAPTCHA 
                                                    sitekey={process.env.REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY}
                                                    onChange={handleChangeCaptcha}
                                                    ref={captchaRef}
                                                />
                                            </Center>

                                            <Button my={4} colorScheme='green' width="full" type='submit' isDisabled={!valid || !isHuman}
                                                isLoading={isProcessing}
                                                loadingText="Đang xử lý"
                                            >
                                                Đăng ký
                                            </Button>
                                        </Flex>
                                    </form>
                                )
                            }}
                        />
                    </ModalBody>
                </ModalContent>
            </Modal>

            {/* {
                isProcessing &&
                <Center>
                    <CircularProgress position="fixed" top="20vh" isIndeterminate size="200px" zIndex={9999}>
                        <CircularProgressLabel fontSize="24px" fontWeight="700" color="#2196f3">Đang xử lý ...</CircularProgressLabel>
                    </CircularProgress>
                </Center>
            } */}
        </React.Fragment>
    )
}

export default RegisterCompanyModal;